import React, { useState } from "react";
import { toast } from "sonner";
import { HeartHandshake, ShieldAlert, ShieldCheck, Eye, EyeOff, RefreshCw, Loader2, CheckCircle2, XCircle, Quote } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const EPIC = "bg-white rounded-3xl border border-slate-200/80 shadow-[0_4px_20px_-4px_rgba(15,23,42,0.06)]";
const ASPECTS = {
  recognition: "Recognition", active_listening: "Active listening", clarity: "Clarity", patience: "Patience", questioning: "Questioning",
  no_pressure: "No pressure", transparency: "Transparency", checking_understanding: "Checked understanding", respect_for_decision: "Respect for decision", knowing_when_to_stop: "Knowing when to stop",
};
const tone = (n) => (n >= 80 ? "text-emerald-600" : n >= 60 ? "text-amber-600" : "text-red-600");
const bar = (n) => (n >= 80 ? "bg-emerald-500" : n >= 60 ? "bg-amber-500" : "bg-red-500");

function List({ items, icon: Icon, cls, testid }) {
  if (!items?.length) return <p className="text-xs text-slate-400 mt-2">Nothing noted.</p>;
  return (
    <ul className="mt-2 space-y-1.5" data-testid={testid}>
      {items.map((t, i) => <li key={`${i}-${String(t).slice(0, 20)}`} className={`text-sm text-slate-700 flex gap-2 rounded-xl border px-3 py-2 ${cls}`}><Icon className="w-4 h-4 shrink-0 mt-0.5" />{t}</li>)}
    </ul>
  );
}

function Moment({ m, i, sessionId, personaName }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(m.retry || null);
  const submit = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try { setResult((await api.post(`/roleplay/${sessionId}/retry`, { index: i, text })).data); }
    catch { toast.error("Couldn't score that attempt. Try again."); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-2xl border border-rose-200/70 bg-rose-50/40 p-4" data-testid={`pressure-moment-${i}`}>
      <div className="text-[10px] font-mono uppercase tracking-wider text-rose-600">Moment {i + 1}</div>
      <p className="text-sm text-slate-900 mt-1 flex gap-2"><Quote className="w-4 h-4 text-rose-400 shrink-0 mt-0.5" /><span><span className="font-semibold">{personaName}:</span> “{m.customer_said}”</span></p>
      <p className="text-sm text-slate-600 mt-2"><span className="font-semibold text-slate-800">You said:</span> “{m.you_said}”</p>
      <p className="text-xs text-rose-700 mt-1">{m.why}</p>
      <p className="text-sm text-emerald-800 mt-2 rounded-xl bg-emerald-50 border border-emerald-200/70 px-3 py-2"><span className="font-semibold">Try instead:</span> “{m.try_instead}”</p>
      <div className="mt-3 pt-3 border-t border-rose-200/60">
        <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500 flex items-center gap-1.5"><RefreshCw className="w-3 h-3" /> Comeback drill — your second go</div>
        {result ? (
          <div className={`mt-2 rounded-xl border px-3 py-2.5 ${result.better ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"}`} data-testid={`retry-result-${i}`}>
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
              {result.better ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <XCircle className="w-4 h-4 text-amber-600" />}
              {result.better ? "Better judgement" : "Not there yet"}
              {result.score_before != null && <span className="ml-auto text-xs font-mono text-slate-500">{result.score_before} → <span className={tone(result.score_after)}>{result.score_after}</span></span>}
            </div>
            <p className="text-xs text-slate-500 mt-1">You said: “{result.text}”</p>
            <p className="text-sm text-slate-700 mt-1.5 leading-relaxed">{result.verdict}</p>
            {result.model_line && <p className="text-xs text-emerald-800 mt-1.5"><span className="font-semibold">Model line:</span> “{result.model_line}”</p>}
            <button onClick={() => setResult(null)} className="text-xs text-slate-500 hover:text-slate-900 underline mt-2" data-testid={`retry-again-${i}`}>Try once more</button>
          </div>
        ) : (
          <div className="flex gap-2 mt-2">
            <Input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submit()} placeholder={`What would you say to ${personaName} now?`} className="h-11 rounded-full bg-white" data-testid={`retry-input-${i}`} />
            <Button onClick={submit} disabled={busy || !text.trim()} className="h-11 rounded-full bg-slate-900 hover:bg-slate-800 text-white px-4" data-testid={`retry-submit-${i}`}>
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Score it"}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

export function VulnerabilityReport({ vh, sessionId, personaName = "The customer" }) {
  if (!vh) return null;
  const p = vh.profile || {};
  const tp = p.trusted_person;
  return (
    <section className={`${EPIC} p-5 border-teal-200 bg-gradient-to-br from-teal-50/60 via-white to-white`} data-testid="vulnerability-report">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="text-base md:text-lg font-bold tracking-tight text-slate-900 flex items-center gap-2"><HeartHandshake className="w-5 h-5 text-teal-600" /> Vulnerability handling</h3>
          <p className="text-xs text-slate-500 mt-1">Responsible selling is the whole test here — the sale only counts if it was earned properly.</p>
        </div>
        <div className="text-right">
          <div className={`text-4xl font-extrabold font-display leading-none ${tone(vh.score)}`} data-testid="vulnerability-score">{vh.score}</div>
          <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500">/100</div>
        </div>
      </div>

      {vh.exploited && (
        <div className="mt-4 flex gap-3 rounded-2xl bg-red-50 border border-red-300 px-4 py-3 text-sm text-red-800" data-testid="exploited-banner">
          <ShieldAlert className="w-5 h-5 shrink-0" />
          <div><span className="font-bold">This does not count as a win.</span> The outcome was reached by confusing, rushing or pressuring a vulnerable customer. Your overall rating has been capped.</div>
        </div>
      )}

      <div className="mt-4 rounded-2xl bg-white border border-slate-200/80 p-4" data-testid="vulnerability-profile">
        <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-2">Who you were really talking to</div>
        <div className="flex flex-wrap gap-1.5 text-xs">
          <span className="px-2 py-1 rounded-full bg-slate-100 border border-slate-200 text-slate-700">Age {p.age}</span>
          <span className="px-2 py-1 rounded-full bg-slate-100 border border-slate-200 text-slate-700 capitalize">{p.severity} vulnerability</span>
          <span className="px-2 py-1 rounded-full bg-slate-100 border border-slate-200 text-slate-700">{p.personality}</span>
          <span className="px-2 py-1 rounded-full bg-slate-100 border border-slate-200 text-slate-700">{p.finances}</span>
          <span className="px-2 py-1 rounded-full bg-slate-100 border border-slate-200 text-slate-700">{tp ? `Trusted ${tp.relation} ${tp.available ? "available" : "not available"}` : "No trusted person available"}</span>
          <span className={`px-2 py-1 rounded-full border font-semibold ${p.capable ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-amber-50 border-amber-200 text-amber-700"}`}>{p.capable ? "Capable of deciding alone" : "Needed considerable care"}</span>
        </div>
        <div className="flex flex-wrap gap-1.5 mt-2">
          {(p.vulnerabilities || []).map((v) => <span key={v.id} className="text-xs px-2 py-1 rounded-full bg-teal-50 border border-teal-200 text-teal-800">{v.label}</span>)}
        </div>
      </div>

      {vh.summary && <p className="mt-4 text-sm text-slate-700 leading-relaxed" data-testid="vulnerability-summary">{vh.summary}</p>}

      <div className="mt-4 grid sm:grid-cols-2 gap-x-6 gap-y-2" data-testid="vulnerability-aspects">
        {Object.entries(ASPECTS).map(([k, label]) => { const n = vh.aspects?.[k]; if (n == null) return null; return (
          <div key={k} className="flex items-center gap-3 text-sm">
            <span className="w-40 text-slate-600 shrink-0">{label}</span>
            <span className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden"><span className={`block h-full rounded-full ${bar(n)}`} style={{ width: `${n}%` }} /></span>
            <span className={`w-8 text-right font-mono text-xs ${tone(n)}`}>{n}</span>
          </div>
        ); })}
      </div>

      <div className="mt-5 grid md:grid-cols-2 gap-5">
        <div><div className="text-sm font-semibold text-slate-900 flex items-center gap-1.5"><Eye className="w-4 h-4 text-emerald-600" /> Signs you noticed</div><List items={vh.signs_noticed} icon={Eye} cls="bg-emerald-50/70 border-emerald-200/60" testid="signs-noticed" /></div>
        <div><div className="text-sm font-semibold text-slate-900 flex items-center gap-1.5"><EyeOff className="w-4 h-4 text-red-500" /> Signs you missed</div><List items={vh.signs_missed} icon={EyeOff} cls="bg-red-50/60 border-red-200/60" testid="signs-missed" /></div>
        <div><div className="text-sm font-semibold text-slate-900">What you did well</div><List items={vh.did_well} icon={CheckCircle2} cls="bg-emerald-50/70 border-emerald-200/60" testid="vh-did-well" /></div>
        <div><div className="text-sm font-semibold text-slate-900">What you missed</div><List items={vh.missed} icon={XCircle} cls="bg-amber-50/70 border-amber-200/60" testid="vh-missed" /></div>
      </div>

      <div className={`mt-5 flex gap-3 rounded-2xl border px-4 py-3 ${vh.right_to_continue ? "bg-emerald-50/70 border-emerald-200" : "bg-amber-50/70 border-amber-200"}`} data-testid="continue-verdict">
        {vh.right_to_continue ? <ShieldCheck className="w-5 h-5 text-emerald-600 shrink-0" /> : <ShieldAlert className="w-5 h-5 text-amber-600 shrink-0" />}
        <div className="text-sm text-slate-800"><span className="font-bold">Would you have been right to continue the sale? {vh.right_to_continue ? "Yes." : "No."}</span> {vh.continue_verdict}</div>
      </div>

      {vh.pressure_moments?.length > 0 && (
        <div className="mt-5">
          <div className="text-sm font-semibold text-slate-900">Where you applied too much pressure — and your second chance</div>
          <p className="text-xs text-slate-500 mt-0.5">Replay each moment. Type what you'd say now and Linden will compare it with your original.</p>
          <div className="mt-3 space-y-3">
            {vh.pressure_moments.map((m, i) => <Moment key={`${i}-${(m.customer_said || "").slice(0, 16)}`} m={m} i={i} sessionId={sessionId} personaName={personaName} />)}
          </div>
        </div>
      )}
    </section>
  );
}
